/**
 * HTTP streaming for chat responses using fetch + ReadableStream.
 *
 * This module provides real streaming from the backend using plain HTTP
 * with newline-delimited JSON (NDJSON).
 */
import { Logger } from 'shared-components/utils';

const logger = new Logger('chatStreaming');
import { CitationProps } from '../CitationPanel';
import { getCitationIcon } from './chatUtils';

const CITATION_DEF_RE = /^\[\d+\]:\s*/;

const STREAM_CLEANERS: Array<{ pattern: RegExp; replacement: string }> = [
  // Strip markdown blockquotes and numbered blockquote-like prefixes (e.g. "3>Cancer Grade").
  // Matches optional digits followed by one or more ">" at start of line.
  { pattern: /^\d*>+\s?/gm, replacement: '' },
  { pattern: /^#{1,6}\s+(.+)$/gm, replacement: '<strong>$1</strong>' },
  { pattern: /\*\*(.+?)\*\*/g, replacement: '<strong>$1</strong>' },
  { pattern: /\*(.+?)\*/g, replacement: '<em>$1</em>' },
  // Strip complete inline citation markers
  { pattern: /<sup>\[\d+\]<\/sup>/g, replacement: '' },
  // Strip partial inline citation markers at the end of text to prevent flicker
  // during streaming (e.g. "<sup>[1", "<sup>[1]", "<sup>" trailing fragments)
  { pattern: /<sup>\[\d*\]?(?:<\/sup>)?$/, replacement: '' },
  { pattern: /<sup>$/, replacement: '' },
  // Strip trailing bare partial citations (e.g. "[", "[1", "[12") that appear
  // mid-stream before the closing "]" arrives in the next chunk
  { pattern: /\[\d*$/, replacement: '' },
  { pattern: /\s*\[?\[?\d+\]?:\s*<?(?:chunk|resource)[:\s][^>\]\n]*>?\]?/g, replacement: '' },
  { pattern: /<chunk:[^>]+>/g, replacement: '' },
  { pattern: /<resource:[^>]+>/g, replacement: '' },
  { pattern: /:?\s*chunk:[\w-]+/g, replacement: '' },
  { pattern: /:?\s*resource:\w+:\d+/g, replacement: '' },
  { pattern: /\[\d+\]/g, replacement: '' },
  { pattern: />\s*\n+\s*</g, replacement: '><' },
  { pattern: /\n{3,}/g, replacement: '\n\n' },
  // Strip markdown horizontal rules (e.g. "---", "***", "___")
  { pattern: /^[-*_]{3,}\s*$/gm, replacement: '' },
];

const cleanStreamText = (text: string): string => {
  let result = text;
  for (const { pattern, replacement } of STREAM_CLEANERS) {
    result = result.replace(pattern, replacement);
  }
  result = result.replace(/^[\s,;:.!?\n\t]+/, '');
  if (result.length > 0 && result[0] >= 'a' && result[0] <= 'z') {
    result = result[0].toUpperCase() + result.slice(1);
  }
  return result;
};

interface StreamingCallbacks {
  onChunk: (text: string) => void;
  onStatus?: (statusText: string) => void;
  onReasoning?: (text: string) => void;
  onReasoningEnd?: () => void;
  onCitations?: (citations: CitationProps[]) => void;
  onConversationId?: (conversationId: number) => void;
  onComplete: (data: {
    conversationId: number;
    title: string;
    citations: CitationProps[];
    type?: string;
    letterHtml?: string;
    isEdit?: boolean;
    finalText?: string;
  }) => void;
  onError: (error: Error) => void;
}

interface StreamEvent {
  type: 'citations' | 'clear' | 'delta' | 'done' | 'error' | 'status' | 'reasoning' | 'reasoning_end' | 'replace';
  text?: string;
  citations?: Array<{
    filename?: string;
    filepath?: string;
    extension?: string;
    documentType?: string;
    resourceId?: string;
    pageNumber?: number;
    documentDate?: string;
    authorName?: string;
    status?: string;
    snippets?: Array<{ content: string; page: string }>;
  }>;
  conversation_id?: number;
  message?: string;
  letter_html?: string;
  response_type?: string;
  display_name?: string;
  is_edit?: boolean;
}

const CHUNK_SIZE = 0x8000; // 32KB chunks to avoid stack overflow

/**
 * Convert backend citation format to frontend CitationProps
 */
const convertCitations = (
  backendCitations: StreamEvent['citations'],
  conversationId: number | string,
): CitationProps[] => {
  if (!backendCitations?.length) return [];

  const citations: CitationProps[] = [];
  let citationIndex = 0;

  for (const citation of backendCitations) {
    const filename = citation.filename || 'Document';
    const filepath = citation.filepath || '';
    const extension = citation.extension || 'pdf';
    const resourceId = citation.resourceId || '';

    const docType = citation.documentType || (extension === 'txt' ? 'text' : 'pdf');

    // For DocumentPart/Note, prefer resourceId so getSummaryDocument can
    // always mint a fresh presigned URL (stored presigned URLs expire).
    let documentUrl = filepath;
    if ((docType === 'DocumentPart' || docType === 'Note') && resourceId) {
      documentUrl = resourceId;
    }

    citations.push({
      title: filename,
      documentUrl,
      documentType: docType,
      dateOfDocument: citation.documentDate || '',
      status: citation.status || '',
      author: citation.authorName || undefined,
      icon: getCitationIcon({ filename, filepath, extension }),
      id: `${conversationId}-${citationIndex}`,
      snippets:
        citation.snippets?.map((snippet) => ({
          page: snippet.page || '',
          text: snippet.content || '',
        })) || [],
    });
    citationIndex++;
  }

  return citations;
};

/**
 * Parse NDJSON stream - handles partial lines across chunks
 */
const parseNDJSON = async function* (
  reader: ReadableStreamDefaultReader<Uint8Array>,
  decoder: TextDecoder,
): AsyncGenerator<StreamEvent> {
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();

    if (done) {
      // Flush any pending incomplete multibyte UTF-8 sequences
      buffer += decoder.decode();
      // Process any remaining buffer
      const trimmed = buffer.trim();
      if (trimmed) {
        try {
          yield JSON.parse(trimmed);
        } catch {
          // Ignore parse errors for incomplete data
          logger.error('parseNDJSON', 'Error parsing NDJSON from remaining buffer');
        }
      }
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');

    // Keep the last (potentially incomplete) line in the buffer
    buffer = lines.pop() || '';

    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed) {
        try {
          yield JSON.parse(trimmed);
        } catch {
          logger.error('parseNDJSON', 'Error parsing NDJSON line');
        }
      }
    }
  }
};

/**
 * Stream chat response using HTTP with ReadableStream.
 *
 * @param prompt - The user's message
 * @param patientId - The patient ID
 * @param conversationId - Optional existing conversation ID
 * @param callbacks - Callbacks for handling streaming events
 * @param abortController - Optional abort controller for cancellation
 * @param activeDocumentHtml - Optional HTML content of the active document being edited
 */
export const streamChatResponse = async (
  prompt: string,
  patientId: number,
  conversationId: number | undefined,
  callbacks: StreamingCallbacks,
  abortController?: AbortController,
  activeDocumentHtml?: string,
): Promise<void> => {
  let receivedCitations: CitationProps[] = [];
  let finalConversationId: number | undefined = conversationId;

  let flushedText = '';
  let pendingLine = '';
  let reasoningText = '';

  const flushLine = (line: string) => {
    if (CITATION_DEF_RE.test(line)) return;
    flushedText += line + '\n';
  };

  const render = () => {
    // Hide the pending line if it looks like a citation definition (e.g. "[1]: chunk:...")
    const hidePending = /^\[\d/.test(pendingLine);
    const visible = hidePending ? flushedText : flushedText + pendingLine;
    callbacks.onChunk(cleanStreamText(visible));
  };

  try {
    // Base64 encode the HTML to avoid WAF blocking patterns in HTML content
    const encodedHtml = activeDocumentHtml
      ? (() => {
          const bytes = new TextEncoder().encode(activeDocumentHtml);
          let binary = '';
          for (let i = 0; i < bytes.length; i += CHUNK_SIZE) {
            binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK_SIZE));
          }
          return btoa(binary);
        })()
      : null;

    const response = await fetch('/server/chat/stream', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      signal: abortController?.signal,
      body: JSON.stringify({
        text: prompt,
        patient_id: patientId,
        conversation_id: conversationId || null,
        active_document_html_b64: encodedHtml,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorJson = JSON.parse(errorText.split('\n')[0]);
        if (errorJson.message) {
          errorMessage = errorJson.message;
        }
      } catch {
        // Use default error message
      }
      throw new Error(errorMessage);
    }

    if (!response.body) {
      throw new Error('Response body is not available');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    for await (const event of parseNDJSON(reader, decoder)) {
      if (abortController?.signal.aborted) {
        reader.cancel();
        throw new Error('Streaming cancelled');
      }

      switch (event.type) {
        case 'citations':
          receivedCitations = convertCitations(event.citations, finalConversationId || conversationId || 'temp');
          callbacks.onCitations?.(receivedCitations);
          break;

        case 'status':
          callbacks.onStatus?.(event.text || '');
          break;

        case 'reasoning':
          reasoningText += event.text || '';
          callbacks.onReasoning?.(reasoningText);
          break;

        case 'reasoning_end':
          reasoningText = '';
          callbacks.onReasoningEnd?.();
          break;

        case 'clear':
          flushedText = '';
          pendingLine = '';
          callbacks.onChunk('');
          break;

        case 'delta': {
          pendingLine += event.text || '';
          while (pendingLine.includes('\n')) {
            const nlIndex = pendingLine.indexOf('\n');
            const line = pendingLine.slice(0, nlIndex);
            pendingLine = pendingLine.slice(nlIndex + 1);
            flushLine(line);
          }
          render();
          break;
        }

        case 'replace': {
          // Engine sends post-processed HTML the moment the model finishes
          // streaming, before citations are built. Swap the entire response
          // text now so the user sees clean output without waiting for the
          // trailing `done` event (which is gated on citation S3 lookups +
          // a DB write).
          const replacement = event.text || '';
          flushedText = replacement;
          pendingLine = '';
          callbacks.onChunk(replacement);
          break;
        }

        case 'done':
          finalConversationId = event.conversation_id ?? finalConversationId;

          if (finalConversationId) {
            callbacks.onConversationId?.(finalConversationId);
          }

          if (!finalConversationId) {
            throw new Error('Server did not return a conversation ID');
          }

          callbacks.onComplete({
            conversationId: finalConversationId,
            title: prompt.substring(0, 255),
            citations: receivedCitations,
            type: event.response_type,
            letterHtml: event.letter_html,
            isEdit: event.is_edit,
            finalText: event.text,
          });
          return;

        case 'error':
          throw new Error(event.message || 'Unknown error');
      }
    }
  } catch (error: unknown) {
    const err = error as Error;
    if (err.name === 'AbortError' || err.message === 'Streaming cancelled') {
      throw new Error('Streaming cancelled');
    }
    callbacks.onError(err);
  }
};
